import React from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
    Grid,
    Form,
    Filter,
    ActionButton,
    useFormContext,
    useFilterContext,
} from "@programari-limit/base-react";
import GridFormField from "components/GridFormField";
import MuiGrid from "@mui/material/Grid";
import { suffixValueFormatter } from "utils/muiUtils";
import {
    columnesAgendaProducte,
    columnesReserves,
    columnesTalonaris,
    columnesUsuaris,
    columnesZones,
} from "sharedAdvancedSearch/advancedSearchColumns";
import { Box, Icon, IconButton, Tooltip } from "@mui/material";
import { SpringFilterQueryBuilder } from "utils/spring-filter-query-builder";
import GridFilterField from "components/GridFilterField";
import { likeAny } from "utils/springFilterUtils";

const ActionModificarEstatContent = () => {
    return (
        <MuiGrid container spacing={2} sx={{ mt: 1 }}>
            <GridFormField name="estat" lg={4} />
            <GridFormField name="facturaNumero" lg={4} />
            <GridFormField name="facturaData" lg={4} />
            <GridFormField name="facturaSerie" lg={12} />
        </MuiGrid>
    );
};

export const ActionFacturarBonoVenedorContent = () => {
    const { data } = useFormContext();
    const isTipusCova = data?.tipus === "COVES";

    return (
        <MuiGrid container spacing={2} sx={{ mt: 1 }}>
            <GridFormField name="tipus" lg={3} />
            {isTipusCova ? (
                <GridFormField name="dataReserva" lg={3} />
            ) : (
                <GridFormField
                    name="disponibilitat"
                    lg={4.5}
                    advancedSearchColumns={columnesAgendaProducte}
                />
            )}

            <GridFormField
                name="venedor"
                lg={isTipusCova ? 6 : 4.5}
                advancedSearchColumns={columnesUsuaris}
            />
            <GridFormField
                name="zona"
                lg={3}
                advancedSearchColumns={columnesZones}
            />
            <GridFormField name="dataFacturacio" lg={3} />
            <GridFormField name="pagamentTipus" lg={2} />
            {data?.pagamentTipus === "EFECTIU" && (
                <GridFormField name="importEfectiu" lg={4} suffix={" €"} />
            )}
            {data.pagamentTipus === "TARGETA" && (
                <GridFormField name="importTargeta" lg={4} suffix={" €"} />
            )}
            {(data.pagamentTipus === "MIXT" || !data.pagamentTipus) && (
                <>
                    <GridFormField name="importEfectiu" lg={2} suffix={" €"} />
                    <GridFormField name="importTargeta" lg={2} suffix={" €"} />
                </>
            )}
            <GridFormField name="adults" lg={2} />
            <GridFormField name="preuAdult" lg={2} suffix={" €"} />
            <GridFormField name="infants" lg={2} />
            <GridFormField name="preuInfant" lg={2} suffix={" €"} />
            <GridFormField name="preuTotal" lg={2} suffix={" €"} disabled />
            <GridFormField name="importTotal" lg={2} suffix={" €"} disabled />
            <GridFormField name="observacions" lg={12} />
        </MuiGrid>
    );
};

export const BonosTalonariForm = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();

    return (
        <Form
            resourceName="talonariBono"
            id={id}
            titleCreate={t("bonosTalonari.create")}
            titleUpdate={t("bonosTalonari.update")}
            onGoBack={() => navigate(-1)}
            // readOnly // TODO: S'habilita de nou l'edicio, baix la responsabilitat de qui ho editi.
        >
            <MuiGrid container spacing={2}>
                <GridFormField name="numero" lg={2} />
                <GridFormField
                    name="disponibilitat"
                    lg={4}
                    advancedSearchColumns={columnesAgendaProducte}
                    advancedSearchMaxWidth="lg"
                />
                <GridFormField name="estat" lg={2} />
                <MuiGrid item lg={4} />
                <GridFormField name="adults" lg={2.25} />
                <GridFormField name="preuAdult" lg={2.25} />
                <GridFormField name="infants" lg={2.25} />
                <GridFormField name="preuInfant" lg={2.25} />
                <GridFormField name="importTotal" lg={3} />
                <GridFormField
                    name="talonari"
                    lg={2.5}
                    advancedSearchColumns={columnesTalonaris}
                    advancedSearchMaxWidth="lg"
                />
                <GridFormField name="facturaData" lg={2} />
                <GridFormField name="facturaNumero" lg={1.5} />
                <GridFormField name="facturaSerie" lg={3} />
                <GridFormField
                    name="reserva"
                    lg={3}
                    advancedSearchColumns={columnesReserves}
                    advancedSearchMaxWidth="xl"
                />
                <GridFormField name="observacions" lg={12} />
            </MuiGrid>
        </Form>
    );
};

// Filtres de la taula
const FilterContent = (props) => {
    const { filterApiRef, setIsAfiliat } = props;
    const { t } = useTranslation();
    const { data } = useFilterContext();

    React.useEffect(() => {
        if (data?.tipus === "AFILIAT") {
            setIsAfiliat(true);
        } else {
            setIsAfiliat(false);
        }
    }, [data]);

    return (
        <MuiGrid container spacing={2}>
            <GridFilterField
                name="dataInici"
                xs={6}
                sm={6}
                md={4}
                lg={2.75}
                xl={2.25}
            />
            <GridFilterField
                name="dataFi"
                xs={6}
                sm={6}
                md={4}
                lg={2.75}
                xl={2.25}
            />
            <GridFilterField
                name="afiliatCodi"
                sm={6}
                md={4}
                lg={3.25}
                xl={3}
            />
            <GridFilterField name="afiliatNom" sm={6} md={4} lg={3.25} xl={3} />
            <GridFilterField name="numeroBono" sm={6} md={4} lg={2} xl={1.5} />
            <GridFilterField name="tipus" sm={6} md={4} lg={2.25} xl={2} />
            <GridFilterField
                name="venedor"
                sm={6}
                md={6}
                lg={4.5}
                xl={3}
                advancedSearchColumns={columnesUsuaris}
            />
            <GridFilterField
                name="estat"
                xs={10}
                sm={4.5}
                md={4}
                lg={2.5}
                xl={2}
            />

            <MuiGrid item lg={0.5}>
                <Tooltip title={t("comu.resetFiltre")}>
                    <IconButton
                        onClick={() => filterApiRef?.current?.doReset()}
                    >
                        <Icon>filter_alt_off</Icon>
                    </IconButton>
                </Tooltip>
            </MuiGrid>
        </MuiGrid>
    );
};

const springFilterBuilder = (data) => {
    const { and, equal, ge, le, exists, like } = SpringFilterQueryBuilder;

    const filter = and(
        ge("talonari.data", data?.dataInici ? data?.dataInici : undefined),
        le("talonari.data", data?.dataFi ? data?.dataFi : undefined),
        like("talonari.afiliatCodi", likeAny(data?.afiliatCodi)),
        like("talonari.afiliatNom", likeAny(data?.afiliatNom)),
        equal("numero", data?.numeroBono),
        equal("talonari.tipus", data?.tipus),
        equal("talonari.venedor.id", data?.venedor?.id),
        equal("estat", data?.estat)
    ).toString();

    return filter;
};

const BonosTalonariFilter = (props) => {
    const { onSpringFilterChange, onFilterInitialized, setIsAfiliat } = props;
    const filterApiRef = React.useRef();

    return (
        <Box sx={{ mb: 2 }}>
            <Filter
                name={"TB_FILTER"}
                resourceName={"talonariBono"}
                persistentState
                springFilterBuilder={springFilterBuilder}
                onSpringFilterChange={onSpringFilterChange}
                onFilterInitialized={onFilterInitialized}
                size="small"
                apiRef={filterApiRef}
                initialOnChangeRequest
            >
                <FilterContent
                    filterApiRef={filterApiRef}
                    setIsAfiliat={setIsAfiliat}
                />
            </Filter>
        </Box>
    );
};

const BonosTalonari = () => {
    const { t } = useTranslation();
    const [fixedFilter, setFixedFilter] = React.useState();
    const [filterInitialized, setFilterInitialized] = React.useState(false);
    const [isAfiliat, setIsAfiliat] = React.useState();
    const gridApiRef = React.useRef();
    const navigate = useNavigate();

    const columns = [
        { name: "numero", flex: 0.75 },
        { name: "disponibilitat", flex: 1 },
        { name: "adults", flex: 0.75 },
        { name: "preuAdult", flex: 1 },
        { name: "infants", flex: 0.75 },
        { name: "preuInfant", flex: 1 },
        {
            name: "importTotal",
            flex: 1,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        { name: "estat", flex: 0.75 },
        { name: "observacions", flex: 1 },
    ];

    const rowAdditionalActions = (params) => {
        const listActions = [];

        listActions.push({
            icon: "edit",
            label: t("comu.detall"),
            onClick: () => navigate(`form/${params?.id}`),
            showInMenu: false,
        });
        listActions.push({
            icon: "book",
            label: t("bonosTalonari.anarTalonari"),
            onClick: () => navigate(`/talonari/form/${params?.talonari?.id}`),
            showInMenu: false,
        });

        listActions.push({
            icon: "point_of_sale",
            showInMenu: true,
            disabled: !params?.talonariVenedor || params?.estat === "FACTURAT",
            rowLinkAction: "TB_FACTURAR_BONO_VENEDOR",
            rowLinkActionOnSuccess: () => gridApiRef?.current?.doRefresh(),
            rowLinkFormChildren: <ActionFacturarBonoVenedorContent />,
            rowLinkFormStaticData: { talonariBonoId: params.id },
            rowLinkFormDialogSize: "lg",
            rowLinkFormInitialOnChangeRequest: true,
        });

        return listActions;
    };

    React.useEffect(() => {
        gridApiRef?.current?.doSelectNone();
    }, [isAfiliat]);

    return (
        <Grid
            apiRef={gridApiRef}
            title={t("bonosTalonari.title")}
            resourceName="talonariBono"
            fixedFilter={fixedFilter}
            findDisabled={!filterInitialized}
            columns={columns}
            rowActionsColumnProps={{
                flex: 0.8,
            }}
            actionExecEnabled
            toolbarHideCreateAction
            rowHideEditViewAction
            rowHideDeleteAction
            rowAdditionalActions={(params) => rowAdditionalActions(params.row)}
            toolbarAdditionalActions={[
                {
                    position: 2,
                    component: (
                        <Tooltip
                            title={
                                isAfiliat
                                    ? ""
                                    : t("bonosTalonari.tooltipModificarEstat")
                            }
                        >
                            <Box component={"span"}>
                                <ActionButton
                                    resourceName="talonariBono"
                                    code="TB_MODIFICAR_ESTAT"
                                    multiple
                                    icon="sync"
                                    variant="contained"
                                    formChildren={
                                        <ActionModificarEstatContent />
                                    }
                                    onSuccess={() => {
                                        gridApiRef?.current?.doRefresh();
                                    }}
                                />
                            </Box>
                        </Tooltip>
                    ),
                },
            ]}
            toolbarAdditionalRow={
                <BonosTalonariFilter
                    onSpringFilterChange={setFixedFilter}
                    onFilterInitialized={() => setFilterInitialized(true)}
                    setIsAfiliat={setIsAfiliat}
                />
            }
            selectable={!!isAfiliat}
            formRoutePath="form"
            density="compact"
            defaultSortModel={[
                {
                    field: "numero",
                    sort: "desc",
                },
            ]}
        />
    );
};

export default BonosTalonari;
